import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { deleteRequest, getRequest, postRequest } from '../../api/ApiController';
import { databaseStoreRef } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';

const EMPTY_VALUES = {
  company_name: '',
  street: '',
  zip: '',
  city: '',
  country: '',
  ico: '',
  dic: '',
  ic_dph: '',
  email: '',
  phone: '',
  website: '',
  contact_person: '',
  secondary_email: '',
  secondary_phone: '',
  bank_name: '',
  iban: '',
  swift: '',
  registry_text: '',
  footer_text: '',
  print_header_center_text: '',
  print_footer_text: '',
  use_unified_header: false,
  use_unified_footer: false,
  show_print_date: false,
  show_page_number: false,
  use_same_header_footer_all_pages: true,
  print_header_layout: null,
  print_footer_layout: null,
  print_assets: {},
  logo_path: '',
  mobile_logo_path: '',
  preferred_language: 'sk',
};

const REQUIRED_FIELD_KEYS = ['company_name', 'street', 'zip', 'city', 'country', 'email', 'phone'];

function validateCompanySettings(values, requiredFieldLabels) {
  const errors = {};
  REQUIRED_FIELD_KEYS.forEach((field) => {
    const message = requiredFieldLabels[field];
    if (message && !String(values?.[field] ?? '').trim()) errors[field] = message;
  });
  return errors;
}

function requiredFieldsTouchedMap() {
  return REQUIRED_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});
}

function createDefaultSection() {
  return { align: 'left', valign: 'top', width_pct: 100, type: 'text', text: '', image_slot: '' };
}

function createDefaultLayout() {
  return {
    first: { sections: [createDefaultSection()] },
    other: { sections: [createDefaultSection()] },
  };
}

function normalizeLayout(layout) {
  const src = layout && typeof layout === 'object' ? layout : createDefaultLayout();
  const normalizePart = (part) => {
    const arr = Array.isArray(part?.sections) ? part.sections : [createDefaultSection()];
    const n = Math.max(1, Math.min(3, arr.length || 1));
    const base = Math.floor(100 / n);
    const rem = 100 - base * n;
    const norm = arr
      .slice(0, 3)
      .map((s, i) => {
        const rawPct = Number(s?.width_pct);
        const fallbackPct = i === n - 1 ? base + rem : base;
        return {
        align: ['left', 'center', 'right'].includes(s?.align) ? s.align : 'left',
        valign: ['top', 'middle', 'bottom'].includes(s?.valign) ? s.valign : 'top',
        width_pct: Number.isFinite(rawPct) && rawPct > 0 ? Math.max(1, Math.min(100, rawPct)) : fallbackPct,
        type: ['text', 'empty', 'image', 'print_date', 'page_x', 'page_xx'].includes(s?.type) ? s.type : 'text',
        text: s?.text ?? '',
        image_slot: s?.image_slot ?? '',
      };});
    return { sections: norm.length ? norm : [createDefaultSection()] };
  };
  return { first: normalizePart(src.first), other: normalizePart(src.other) };
}

/** Same field subset as backend `company_settings_print` in startRequest — updates print payload in the store without re-login. */
function rowToCompanySettingsPrint(row) {
  if (!row) return null;
  return {
    company_name: row.company_name ?? '',
    street: row.street ?? '',
    zip: row.zip ?? '',
    city: row.city ?? '',
    country: row.country ?? '',
    ico: row.ico ?? '',
    dic: row.dic ?? '',
    ic_dph: row.ic_dph ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    secondary_phone: row.secondary_phone ?? '',
    secondary_email: row.secondary_email ?? '',
    website: row.website ?? '',
    bank_name: row.bank_name ?? '',
    iban: row.iban ?? '',
    swift: row.swift ?? '',
    footer_text: row.footer_text ?? '',
    print_header_center_text: row.print_header_center_text ?? '',
    print_footer_text: row.print_footer_text ?? '',
    use_unified_header: !!row.use_unified_header,
    use_unified_footer: !!row.use_unified_footer,
    show_print_date: !!row.show_print_date,
    show_page_number: !!row.show_page_number,
    use_same_header_footer_all_pages: row.use_same_header_footer_all_pages !== false,
    print_header_layout: normalizeLayout(row.print_header_layout),
    print_footer_layout: normalizeLayout(row.print_footer_layout),
    print_assets: row.print_assets && typeof row.print_assets === 'object' ? row.print_assets : {},
    logo_path: row.logo_path ?? '',
    preferred_language: row.preferred_language ?? 'sk',
  };
}

function normalizeRow(row) {
  if (!row) return { ...EMPTY_VALUES };
  return {
    company_name: row.company_name ?? '',
    street: row.street ?? '',
    zip: row.zip ?? '',
    city: row.city ?? '',
    country: row.country ?? '',
    ico: row.ico ?? '',
    dic: row.dic ?? '',
    ic_dph: row.ic_dph ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    website: row.website ?? '',
    contact_person: row.contact_person ?? '',
    secondary_email: row.secondary_email ?? '',
    secondary_phone: row.secondary_phone ?? '',
    bank_name: row.bank_name ?? '',
    iban: row.iban ?? '',
    swift: row.swift ?? '',
    registry_text: row.registry_text ?? '',
    footer_text: row.footer_text ?? '',
    print_header_center_text: row.print_header_center_text ?? '',
    print_footer_text: row.print_footer_text ?? '',
    use_unified_header: !!row.use_unified_header,
    use_unified_footer: !!row.use_unified_footer,
    show_print_date: !!row.show_print_date,
    show_page_number: !!row.show_page_number,
    use_same_header_footer_all_pages: row.use_same_header_footer_all_pages !== false,
    print_header_layout: normalizeLayout(row.print_header_layout),
    print_footer_layout: normalizeLayout(row.print_footer_layout),
    print_assets: row.print_assets && typeof row.print_assets === 'object' ? row.print_assets : {},
    logo_path: row.logo_path ?? '',
    mobile_logo_path: row.mobile_logo_path ?? '',
    preferred_language: row.preferred_language ?? 'sk',
  };
}

function getErrorDetail(res, t) {
  if (res?.errors && typeof res.errors === 'object') {
    const first = Object.values(res.errors)[0];
    if (Array.isArray(first) && first[0]) return String(first[0]);
  }
  if (typeof res?.message === 'string') return res.message;
  return t('CompanySettings.Error.SaveFailed');
}

function buildPayload(values) {
  return {
    company_name: values.company_name,
    street: values.street,
    zip: values.zip,
    city: values.city,
    country: values.country,
    ico: values.ico,
    dic: values.dic,
    ic_dph: values.ic_dph,
    email: values.email,
    phone: values.phone,
    website: values.website,
    contact_person: values.contact_person || null,
    secondary_email: values.secondary_email || null,
    secondary_phone: values.secondary_phone || null,
    bank_name: values.bank_name || null,
    iban: values.iban || null,
    swift: values.swift || null,
    registry_text: values.registry_text || null,
    footer_text: values.footer_text || null,
    print_header_center_text: values.print_header_center_text || null,
    print_footer_text: values.print_footer_text || null,
    use_unified_header: values.use_unified_header,
    use_unified_footer: values.use_unified_footer,
    show_print_date: values.show_print_date,
    show_page_number: values.show_page_number,
    use_same_header_footer_all_pages: values.use_same_header_footer_all_pages !== false,
    print_header_layout: normalizeLayout(values.print_header_layout),
    print_footer_layout: normalizeLayout(values.print_footer_layout),
    print_assets: values.print_assets && typeof values.print_assets === 'object' ? values.print_assets : {},
    logo_path: values.logo_path || null,
    mobile_logo_path: values.mobile_logo_path || null,
    preferred_language: values.preferred_language || 'sk',
  };
}

function syncCompanyPreferredLanguageToCache(row) {
  const value = row?.preferred_language ? String(row.preferred_language).toLowerCase() : '';
  if (value) localStorage.setItem('preferred_language_company', value);
  else localStorage.removeItem('preferred_language_company');
  window.dispatchEvent(new Event('app-locale-preferences-updated'));
}

function FieldRow({ label, children, className = '', error = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-sm font-medium text-gray-800">{label}</label>
      {children}
      {error ? <small className="text-red-600">{error}</small> : null}
    </div>
  );
}

function PrintPreview({ values, printAssetUrls = {}, t }) {
  const sampleDate = useMemo(() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }, []);

  const variant = values.__previewVariant === 'other' ? 'other' : 'first';
  const previewPageNumber = variant === 'other' ? 2 : 1;
  const headerLayout = normalizeLayout(values.print_header_layout)[variant];
  const footerLayout = normalizeLayout(values.print_footer_layout)[variant];
  const printAssets = values.print_assets && typeof values.print_assets === 'object' ? values.print_assets : {};

  const sectionClass = (align) => (align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left');
  const sectionAlignItemsClass = (align) => (align === 'center' ? 'items-center' : align === 'right' ? 'items-end' : 'items-start');
  const sectionVAlignClass = (valign) => (valign === 'middle' ? 'items-center' : valign === 'bottom' ? 'items-end' : 'items-start');
  const sectionSizeStyle = (sec, count) => {
    const pct = Math.max(1, Math.min(100, Number(sec?.width_pct) || 100));
    if (count <= 1) {
      return { flexBasis: `${pct}%`, maxWidth: `${pct}%` };
    }
    const gapRem = 0.5; // Tailwind gap-2
    const deduction = ((count - 1) * gapRem) / count;
    const basis = `calc(${pct}% - ${deduction}rem)`;
    return { flexBasis: basis, maxWidth: basis };
  };

  const renderPreviewSection = (sec, idx, fallbackSlot = '') => {
    const slot = ((sec.image_slot || '').trim() || fallbackSlot || '').trim();
    const assetPath = slot ? printAssets[slot] : '';
    const assetUrl = slot ? (printAssetUrls[slot] || '') : '';
    if (sec.type === 'empty') {
      return <div key={idx} className="w-full min-h-[1px]" />;
    }
    if (sec.type === 'image') {
      return (
        <div key={idx} className={`min-w-0 w-full flex flex-col gap-1 ${sectionClass(sec.align)} ${sectionAlignItemsClass(sec.align)}`}>
          {assetUrl ? <img src={assetUrl} alt={t('CompanySettings.Alt.PrintAsset')} className="max-h-10 w-auto object-contain" /> : (assetPath ? <span className="text-[11px] text-gray-600 truncate max-w-full" title={String(assetPath)}>{t('CompanySettings.Preview.ImagePlaceholder')}</span> : null)}
        </div>
      );
    }
    if (sec.type === 'print_date') {
      return <div key={idx} className={`text-[11px] text-gray-700 w-full ${sectionClass(sec.align)}`}>{t('CompanySettings.Preview.PrintDateLine').replace('{date}', sampleDate)}</div>;
    }
    if (sec.type === 'page_x') {
      return <div key={idx} className={`text-[11px] text-gray-700 w-full ${sectionClass(sec.align)}`}>{t('CompanySettings.Preview.PageNumberOnly').replace('{n}', String(previewPageNumber))}</div>;
    }
    if (sec.type === 'page_xx') {
      return <div key={idx} className={`text-[11px] text-gray-700 w-full ${sectionClass(sec.align)}`}>{t('CompanySettings.Preview.PageWithTotal').replace('{current}', String(previewPageNumber)).replace('{total}', '3')}</div>;
    }
    const txt = (sec.text || '').trim();
    return txt ? <div key={idx} className={`text-[11px] text-gray-700 whitespace-pre-wrap w-full ${sectionClass(sec.align)}`}>{txt}</div> : <div key={idx} className="w-full" />;
  };

  return (
    <div
      className={`bg-white border border-gray-300 shadow-md rounded-sm p-6 min-h-[min(72vh,780px)] flex flex-col text-sm text-zinc-800 ${
        'border-t-4 border-t-zinc-700 border-b-4 border-b-zinc-700'
      }`}
    >
      <header className="border-b border-gray-200 pb-4 mb-3">
        <div className="flex flex-wrap gap-2">
          {headerLayout.sections.map((sec, i) => (
            <div key={i} className={`border border-gray-200 rounded px-2 py-1 bg-white min-h-[44px] flex ${sectionVAlignClass(sec.valign)}`} style={sectionSizeStyle(sec, headerLayout.sections.length)}>
              {renderPreviewSection(sec, i, `print_header_layout_${variant}_${i + 1}`)}
            </div>
          ))}
        </div>
      </header>

      <div className="mb-4 flex flex-col gap-1 border-t border-gray-200 pt-3 sm:flex-row sm:items-baseline sm:justify-between">
        <h2 className="text-lg font-bold leading-tight text-zinc-900 sm:text-xl">{t('CompanySettings.Preview.HeaderTitle')}</h2>
      </div>

      <div className="flex-1 rounded border border-dashed border-gray-200 bg-gray-50/80 p-4 text-gray-600">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{t('CompanySettings.Preview.DocumentContentLabel')}</p>
        <p className="mb-3">
          {t('CompanySettings.Preview.DocumentContentBody')}
        </p>
        <div className="h-24 rounded bg-white border border-gray-200" />
      </div>

      <footer className="mt-auto border-t border-gray-200 pt-3">
        <div className="flex flex-wrap gap-2 mb-2">
          {footerLayout.sections.map((sec, i) => (
            <div key={i} className={`border border-gray-200 rounded px-2 py-1 bg-white min-h-[38px] flex ${sectionVAlignClass(sec.valign)}`} style={sectionSizeStyle(sec, footerLayout.sections.length)}>
              {renderPreviewSection(sec, i, `print_footer_layout_${variant}_${i + 1}`)}
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
}

export default function CompanySettings() {
  const { toast, openChosenNotificationPreferences } = useContext(UtilContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const perms = user?.permissions ?? [];
  const canAddCompanySettings = perms.includes('add company settings');
  const canEditCompanySettings = perms.includes('edit company settings');

  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingMobileLogo, setUploadingMobileLogo] = useState(false);
  const [uploadingPrintAssetSlot, setUploadingPrintAssetSlot] = useState('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [mobileLogoPreviewUrl, setMobileLogoPreviewUrl] = useState('');
  const [printAssetPreviewUrls, setPrintAssetPreviewUrls] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const canMutateCompanySettings = useMemo(
    () => (recordId == null && canAddCompanySettings) || (recordId != null && canEditCompanySettings),
    [recordId, canAddCompanySettings, canEditCompanySettings]
  );
  const fieldsLocked = loading || !canMutateCompanySettings;

  const requiredFieldLabels = useMemo(
    () =>
      REQUIRED_FIELD_KEYS.reduce((acc, key) => {
        acc[key] = t(`CompanySettings.Required.${key}`);
        return acc;
      }, {}),
    [locale, t]
  );
  const requiredFieldLabelsRef = useRef(requiredFieldLabels);
  requiredFieldLabelsRef.current = requiredFieldLabels;

  const printSectionTypes = useMemo(
    () => [
      { label: t('CompanySettings.PrintSection.text'), value: 'text' },
      { label: t('CompanySettings.PrintSection.empty'), value: 'empty' },
      { label: t('CompanySettings.PrintSection.image'), value: 'image' },
      { label: t('CompanySettings.PrintSection.print_date'), value: 'print_date' },
      { label: t('CompanySettings.PrintSection.page_x'), value: 'page_x' },
      { label: t('CompanySettings.PrintSection.page_xx'), value: 'page_xx' },
    ],
    [locale, t]
  );

  const formik = useFormik({
    initialValues: EMPTY_VALUES,
    validate: (values) => validateCompanySettings(values, requiredFieldLabelsRef.current),
    onSubmit: async (values, { setValues }) => {
      const payload = buildPayload(values);
      const wasEdit = recordId != null;
      try {
        let res;
        if (recordId != null) {
          res = await postRequest(`/api/company_settings/update/${recordId}`, payload, true);
        } else {
          res = await postRequest('/api/company_settings', payload, true);
        }
        if (res.status === 200 || res.status === 201) {
          const row = res.data;
          if (row?.id) {
            setRecordId(row.id);
            setValues(normalizeRow(row));
            syncCompanyPreferredLanguageToCache(row);
            databaseStoreRef.current.dispatch({
              type: 'load_company_settings_print',
              newCompanySettingsPrint: rowToCompanySettingsPrint(row),
            });
            if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
              databaseStoreRef.current.dispatch({
                type: 'load_branding',
                newBranding: res.branding,
              });
              setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
              setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
              setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
            }
          }
          toast?.current?.show({
            severity: 'success',
            summary: t('CompanySettings.Toast.ConfirmedTitle'),
            life: 3500,
            detail: wasEdit ? t('CompanySettings.Toast.SavedDetail') : t('CompanySettings.Toast.CreatedDetail'),
          });
        } else if (res?.status === 422 && res?.errors && typeof res.errors === 'object') {
          const nextErrors = {};
          Object.entries(res.errors).forEach(([key, val]) => {
            if (Array.isArray(val) && val[0]) nextErrors[key] = String(val[0]);
          });
          setSubmitAttempted(true);
          formik.setTouched({ ...requiredFieldsTouchedMap() }, false);
          formik.setErrors(nextErrors);
        } else {
          toast?.current?.show({
            severity: 'error',
            summary: t('Common.Error'),
            life: 5000,
            detail: getErrorDetail(res, t),
          });
        }
      } catch (e) {
        if (e?.status === 422 && e?.errors && typeof e.errors === 'object') {
          const nextErrors = {};
          Object.entries(e.errors).forEach(([key, val]) => {
            if (Array.isArray(val) && val[0]) nextErrors[key] = String(val[0]);
          });
          setSubmitAttempted(true);
          formik.setTouched({ ...requiredFieldsTouchedMap() }, false);
          formik.setErrors(nextErrors);
          return;
        }
        toast?.current?.show({
          severity: 'error',
          summary: t('Common.Error'),
          life: 5000,
          detail: e?.message || t('CompanySettings.Error.SaveFailed'),
        });
      }
    },
  });

  const fieldError = (name) => ((submitAttempted || formik.touched?.[name]) && formik.errors?.[name] ? formik.errors[name] : '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getRequest('/api/company_settings');
        if (cancelled) return;
        if (res.status === 200 && res.data != null) {
          setRecordId(res.data.id);
          formik.setValues(normalizeRow(res.data));
          syncCompanyPreferredLanguageToCache(res.data);
          databaseStoreRef.current.dispatch({
            type: 'load_company_settings_print',
            newCompanySettingsPrint: rowToCompanySettingsPrint(res.data),
          });
          if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
            databaseStoreRef.current.dispatch({
              type: 'load_branding',
              newBranding: res.branding,
            });
            setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
            setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
            setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
          }
        } else {
          setRecordId(null);
          formik.setValues({ ...EMPTY_VALUES });
          syncCompanyPreferredLanguageToCache(null);
          databaseStoreRef.current.dispatch({
            type: 'load_company_settings_print',
            newCompanySettingsPrint: null,
          });
          if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
            databaseStoreRef.current.dispatch({
              type: 'load_branding',
              newBranding: res.branding,
            });
            setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
            setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
            setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
          }
        }
      } catch (e) {
        if (!cancelled) {
          toast?.current?.show({
            severity: 'error',
            summary: t('Common.Error'),
            life: 5000,
            detail: e?.message || t('CompanySettings.Toast.LoadFailedDetail'),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (recordId == null) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('CompanySettings.Toast.SaveFirstTitle'),
        life: 4000,
        detail: t('CompanySettings.Toast.SaveFirstDetail'),
      });
      return;
    }
    const fd = new FormData();
    fd.append('logo', file);
    setUploadingLogo(true);
    try {
      const res = await postRequest(`/api/company_settings/${recordId}/logo`, fd, true);
      if (res?.status === 200 && res?.data) {
        formik.setValues(normalizeRow(res.data));
        databaseStoreRef.current.dispatch({
          type: 'load_company_settings_print',
          newCompanySettingsPrint: rowToCompanySettingsPrint(res.data),
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          databaseStoreRef.current.dispatch({
            type: 'load_branding',
            newBranding: res.branding,
          });
          setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
          setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        }
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.LogoSavedTitle'),
          life: 3000,
          detail: t('CompanySettings.Toast.LogoSavedDetail'),
        });
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        life: 5000,
        detail: e?.message || t('CompanySettings.Toast.LogoUploadFailedDetail'),
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleMobileLogoUpload = async (file) => {
    if (!file) return;
    if (recordId == null) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('CompanySettings.Toast.SaveFirstTitle'),
        life: 4000,
        detail: t('CompanySettings.Toast.SaveFirstDetail'),
      });
      return;
    }
    const fd = new FormData();
    fd.append('logo', file);
    fd.append('logo_target', 'mobile');
    setUploadingMobileLogo(true);
    try {
      const res = await postRequest(`/api/company_settings/${recordId}/logo`, fd, true);
      if (res?.status === 200 && res?.data) {
        formik.setValues(normalizeRow(res.data));
        databaseStoreRef.current.dispatch({
          type: 'load_company_settings_print',
          newCompanySettingsPrint: rowToCompanySettingsPrint(res.data),
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          databaseStoreRef.current.dispatch({
            type: 'load_branding',
            newBranding: res.branding,
          });
          setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
          setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        }
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.MobileLogoSavedTitle'),
          life: 3000,
          detail: t('CompanySettings.Toast.MobileLogoSavedDetail'),
        });
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        life: 5000,
        detail: e?.message || t('CompanySettings.Toast.MobileLogoUploadFailedDetail'),
      });
    } finally {
      setUploadingMobileLogo(false);
    }
  };

  const handleMobileLogoDelete = async () => {
    if (recordId == null) return;
    setUploadingMobileLogo(true);
    try {
      const res = await deleteRequest(`/api/company_settings/${recordId}/logo?target=mobile`);
      if (res?.status === 200 && res?.data) {
        formik.setValues(normalizeRow(res.data));
        databaseStoreRef.current.dispatch({
          type: 'load_company_settings_print',
          newCompanySettingsPrint: rowToCompanySettingsPrint(res.data),
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          databaseStoreRef.current.dispatch({
            type: 'load_branding',
            newBranding: res.branding,
          });
          setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
          setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        } else {
          setMobileLogoPreviewUrl('');
        }
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.MobileLogoRemovedTitle'),
          life: 3000,
          detail: t('CompanySettings.Toast.MobileLogoRemovedDetail'),
        });
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        life: 5000,
        detail: e?.message || t('CompanySettings.Toast.MobileLogoRemoveFailedDetail'),
      });
    } finally {
      setUploadingMobileLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (recordId == null) return;
    setUploadingLogo(true);
    try {
      const res = await deleteRequest(`/api/company_settings/${recordId}/logo`);
      if (res?.status === 200 && res?.data) {
        formik.setValues(normalizeRow(res.data));
        databaseStoreRef.current.dispatch({
          type: 'load_company_settings_print',
          newCompanySettingsPrint: rowToCompanySettingsPrint(res.data),
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          databaseStoreRef.current.dispatch({
            type: 'load_branding',
            newBranding: res.branding,
          });
          setLogoPreviewUrl((res?.branding?.logo_url || '').trim());
          setMobileLogoPreviewUrl((res?.branding?.mobile_logo_url || '').trim());
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        } else {
          setLogoPreviewUrl('');
          setMobileLogoPreviewUrl('');
          setPrintAssetPreviewUrls({});
        }
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.LogoRemovedTitle'),
          life: 3000,
          detail: t('CompanySettings.Toast.LogoRemovedDetail'),
        });
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        life: 5000,
        detail: e?.message || t('CompanySettings.Toast.LogoRemoveFailedDetail'),
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const updateLayoutSectionCount = (layoutKey, variant, count) => {
    const n = Math.max(1, Math.min(3, Number(count) || 1));
    const current = normalizeLayout(formik.values[layoutKey]);
    const source = current?.[variant]?.sections || [];
    const nextSections = [];
    for (let i = 0; i < n; i += 1) {
      nextSections.push(source[i] ? { ...source[i] } : createDefaultSection());
    }
    const base = Math.floor(100 / n);
    const rem = 100 - base * n;
    const rebalanced = nextSections.map((s, i) => ({ ...s, width_pct: i === n - 1 ? base + rem : base }));
    formik.setFieldValue(layoutKey, {
      ...current,
      [variant]: { sections: rebalanced },
    });
  };

  const updateLayoutSection = (layoutKey, variant, idx, patch) => {
    const current = normalizeLayout(formik.values[layoutKey]);
    const sections = [...(current?.[variant]?.sections || [createDefaultSection()])];
    const src = sections[idx] || createDefaultSection();
    sections[idx] = { ...src, ...patch };
    formik.setFieldValue(layoutKey, {
      ...current,
      [variant]: { sections },
    });
  };

  const ensureSectionSlot = (layoutKey, variant, idx) => {
    const current = normalizeLayout(formik.values[layoutKey]);
    const sections = [...(current?.[variant]?.sections || [createDefaultSection()])];
    const src = sections[idx] || createDefaultSection();
    if ((src.image_slot || '').trim() !== '') return src.image_slot;
    const slot = `${layoutKey}_${variant}_${idx + 1}`;
    sections[idx] = { ...src, image_slot: slot };
    formik.setFieldValue(layoutKey, {
      ...current,
      [variant]: { sections },
    });
    return slot;
  };

  const handlePrintAssetUpload = async (layoutKey, variant, idx, file) => {
    if (!file || recordId == null) return;
    const slot = ensureSectionSlot(layoutKey, variant, idx);
    const fd = new FormData();
    fd.append('slot', slot);
    fd.append('asset', file);
    setUploadingPrintAssetSlot(slot);
    try {
      const res = await postRequest(`/api/company_settings/${recordId}/print-asset`, fd, true);
      if (res?.status === 200 && res?.data) {
        const normalized = normalizeRow(res.data);
        formik.setFieldValue('print_assets', normalized.print_assets ?? {});
        formik.setFieldValue('logo_path', normalized.logo_path ?? '');
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.SectionImageUploadedTitle'),
          detail: t('CompanySettings.Toast.SectionImageUploadedDetail'),
          life: 2500,
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        }
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        detail: e?.message || t('CompanySettings.Toast.SectionImageUploadFailedDetail'),
        life: 4500,
      });
    } finally {
      setUploadingPrintAssetSlot('');
    }
  };

  const handlePrintAssetDelete = async (slot) => {
    if (!slot || recordId == null) return;
    setUploadingPrintAssetSlot(slot);
    try {
      const res = await deleteRequest(`/api/company_settings/${recordId}/print-asset?slot=${encodeURIComponent(slot)}`);
      if (res?.status === 200 && res?.data) {
        const normalized = normalizeRow(res.data);
        formik.setFieldValue('print_assets', normalized.print_assets ?? {});
        formik.setFieldValue('logo_path', normalized.logo_path ?? '');
        toast?.current?.show({
          severity: 'success',
          summary: t('CompanySettings.Toast.SectionImageRemovedTitle'),
          detail: t('CompanySettings.Toast.SectionImageRemovedDetail'),
          life: 2500,
        });
        if (Object.prototype.hasOwnProperty.call(res || {}, 'branding')) {
          setPrintAssetPreviewUrls(res?.branding?.print_asset_urls || {});
        }
      } else {
        throw new Error(getErrorDetail(res, t));
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        detail: e?.message || t('CompanySettings.Toast.SectionImageRemoveFailedDetail'),
        life: 4500,
      });
    } finally {
      setUploadingPrintAssetSlot('');
    }
  };

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full max-w-[1600px] ml-0 mr-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('CompanySettings.Title')}</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              {t('CompanySettings.Subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:ml-auto sm:items-end">
            {perms.includes('access company settings') && (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-zinc-800 rounded-md hover:bg-zinc-700 h-10"
                onClick={() => openChosenNotificationPreferences?.('CompanySettings')}
              >
                <span className="pi pi-envelope" aria-hidden />
                {t('ServiceTask.Notifications')}
              </button>
            )}
          <div className="w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-end gap-2">
              <div className="w-full sm:w-56">
                <label className="text-xs text-gray-500 block mb-1">{t('CompanySettings.Label.PreferredLanguage')}</label>
                <select
                  value={formik.values.preferred_language || 'sk'}
                  onChange={(e) => formik.setFieldValue('preferred_language', e.target.value)}
                  disabled={fieldsLocked}
                  className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm bg-white"
                >
                  <option value="sk">Slovensky (SK)</option>
                  <option value="en">English (EN)</option>
                  <option value="de">Deutsch (DE)</option>
                </select>
              </div>
              <Button
                type="button"
                label={t('Common.Save')}
                icon="pi pi-save"
                onClick={() => {
                  setSubmitAttempted(true);
                  formik.setTouched({ ...requiredFieldsTouchedMap() }, false);
                  formik.handleSubmit();
                }}
                disabled={loading || formik.isSubmitting || !canMutateCompanySettings}
                className="shrink-0 p-2"
              />
            </div>
          </div>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">{t('CompanySettings.Loading')}</p>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
              <div className="xl:col-span-5 flex flex-col gap-4">
                <section className="p-6 bg-white rounded-md shadow-md w-full">
                  <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('CompanySettings.Section.RequiredFields')}</h2>
                  <div className="flex flex-col gap-3">
                    <FieldRow label={t('CompanySettings.Label.CompanyName')} error={fieldError('company_name')}>
                      <InputText name="company_name" value={formik.values.company_name} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('company_name') ? 'border-red-500' : 'border-gray-300'}`} />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.Street')} error={fieldError('street')}>
                      <InputText name="street" value={formik.values.street} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('street') ? 'border-red-500' : 'border-gray-300'}`} />
                    </FieldRow>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FieldRow label={t('CompanySettings.Label.Zip')} error={fieldError('zip')}>
                        <InputText name="zip" value={formik.values.zip} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('zip') ? 'border-red-500' : 'border-gray-300'}`} />
                      </FieldRow>
                      <FieldRow label={t('CompanySettings.Label.City')} error={fieldError('city')}>
                        <InputText name="city" value={formik.values.city} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('city') ? 'border-red-500' : 'border-gray-300'}`} />
                      </FieldRow>
                    </div>
                    <FieldRow label={t('CompanySettings.Label.Country')} error={fieldError('country')}>
                      <InputText name="country" value={formik.values.country} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('country') ? 'border-red-500' : 'border-gray-300'}`} />
                    </FieldRow>
                    <FieldRow label={t('Common.Email')} error={fieldError('email')}>
                      <InputText name="email" value={formik.values.email} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('email') ? 'border-red-500' : 'border-gray-300'}`} />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.Phone')} error={fieldError('phone')}>
                      <InputText name="phone" value={formik.values.phone} onChange={formik.handleChange} disabled={fieldsLocked} className={`w-full border rounded-md ${fieldError('phone') ? 'border-red-500' : 'border-gray-300'}`} />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.WebsiteOptional')}>
                      <InputText name="website" value={formik.values.website} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <div>
                      <label className="text-sm font-medium text-gray-800 block mb-1">{t('CompanySettings.Label.LogoOptional')}</label>
                      <div className="rounded-md border border-gray-200 bg-gray-50/80 p-3 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            disabled={!canMutateCompanySettings || recordId == null || uploadingLogo}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleLogoUpload(file);
                              e.currentTarget.value = '';
                            }}
                            className="text-xs"
                          />
                          <Button
                            type="button"
                            label={t('CompanySettings.Button.RemoveLogo')}
                            icon="pi pi-times"
                            className="p-button-text p-button-sm p-2"
                            disabled={!canMutateCompanySettings || uploadingLogo || !(formik.values.logo_path || '').trim()}
                            onClick={handleLogoDelete}
                          />
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                          {t('CompanySettings.Hint.AllowedLogoFormats')}
                        </div>
                        <InputText
                          disabled
                          readOnly
                          value={formik.values.logo_path ?? ''}
                          className="w-full font-mono text-xs mt-2"
                          placeholder={t('CompanySettings.Placeholder.LogoPath')}
                        />
                        {logoPreviewUrl ? (
                          <div className="mt-3 rounded border border-gray-200 bg-white p-2 inline-flex items-center justify-center min-h-[70px]">
                            <img src={logoPreviewUrl} alt={t('CompanySettings.Alt.LogoPreview')} className="max-h-16 w-auto object-contain" />
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-800 block mb-1">{t('CompanySettings.Label.MobileLogoOptional')}</label>
                      <div className="rounded-md border border-gray-200 bg-gray-50/80 p-3 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            disabled={!canMutateCompanySettings || recordId == null || uploadingMobileLogo || uploadingLogo}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleMobileLogoUpload(file);
                              e.currentTarget.value = '';
                            }}
                            className="text-xs"
                          />
                          <Button
                            type="button"
                            label={t('CompanySettings.Button.RemoveMobileLogo')}
                            icon="pi pi-times"
                            className="p-button-text p-button-sm p-2"
                            disabled={!canMutateCompanySettings || uploadingMobileLogo || uploadingLogo || !(formik.values.mobile_logo_path || '').trim()}
                            onClick={handleMobileLogoDelete}
                          />
                        </div>
                        <div className="mt-2 text-xs text-gray-600">{t('CompanySettings.Hint.MobileLogo')}</div>
                        <div className="mt-2 text-xs text-gray-600">{t('CompanySettings.Hint.AllowedLogoFormats')}</div>
                        <InputText
                          disabled
                          readOnly
                          value={formik.values.mobile_logo_path ?? ''}
                          className="w-full font-mono text-xs mt-2"
                          placeholder={t('CompanySettings.Placeholder.MobileLogoPath')}
                        />
                        {mobileLogoPreviewUrl ? (
                          <div className="mt-3 rounded border border-gray-700 bg-zinc-900 p-3 inline-flex items-center justify-center min-h-[70px]">
                            <img src={mobileLogoPreviewUrl} alt={t('CompanySettings.Alt.MobileLogoPreview')} className="max-h-16 w-auto object-contain" />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="p-6 bg-white rounded-md shadow-md w-full">
                  <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('CompanySettings.Section.OptionalFields')}</h2>
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <FieldRow label={t('CompanySettings.Label.Ico')}>
                        <InputText name="ico" value={formik.values.ico} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                      </FieldRow>
                      <FieldRow label={t('CompanySettings.Label.Dic')}>
                        <InputText name="dic" value={formik.values.dic} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                      </FieldRow>
                      <FieldRow label={t('CompanySettings.Label.IcDph')}>
                        <InputText name="ic_dph" value={formik.values.ic_dph} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                      </FieldRow>
                    </div>
                    <FieldRow label={t('CompanySettings.Label.ContactPerson')}>
                      <InputText name="contact_person" value={formik.values.contact_person} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.SecondaryEmail')}>
                      <InputText name="secondary_email" value={formik.values.secondary_email} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.SecondaryPhone')}>
                      <InputText name="secondary_phone" value={formik.values.secondary_phone} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.BankName')}>
                      <InputText name="bank_name" value={formik.values.bank_name} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.Iban')}>
                      <InputText name="iban" value={formik.values.iban} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.Swift')}>
                      <InputText name="swift" value={formik.values.swift} onChange={formik.handleChange} disabled={fieldsLocked} className="w-full border border-gray-300 rounded-md" />
                    </FieldRow>
                    <FieldRow label={t('CompanySettings.Label.RegistryText')}>
                      <InputTextarea
                        name="registry_text"
                        value={formik.values.registry_text}
                        onChange={formik.handleChange}
                        disabled={fieldsLocked}
                        rows={4}
                        className="w-full border border-gray-300 rounded-md"
                      />
                    </FieldRow>
                  </div>
                </section>

                <section className="p-6 bg-white rounded-md shadow-md w-full">
                  <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('CompanySettings.Section.PrintAndBranding')}</h2>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2 bg-gray-50">
                      <span className="text-sm font-medium text-gray-800">{t('CompanySettings.SameHeaderFooterAllPages')}</span>
                      <InputSwitch
                        checked={formik.values.use_same_header_footer_all_pages !== false}
                        onChange={(e) => formik.setFieldValue('use_same_header_footer_all_pages', !!e.value)}
                        disabled={fieldsLocked}
                      />
                    </div>
                    {[
                      { layoutKey: 'print_header_layout', title: t('CompanySettings.Layout.HeaderFirst'), variant: 'first' },
                      { layoutKey: 'print_footer_layout', title: t('CompanySettings.Layout.FooterFirst'), variant: 'first' },
                      ...(formik.values.use_same_header_footer_all_pages !== false
                        ? []
                        : [
                            { layoutKey: 'print_header_layout', title: t('CompanySettings.Layout.HeaderOther'), variant: 'other' },
                            { layoutKey: 'print_footer_layout', title: t('CompanySettings.Layout.FooterOther'), variant: 'other' },
                          ]),
                    ].map(({ layoutKey, title, variant }) => {
                      const layout = normalizeLayout(formik.values[layoutKey]);
                      const sections = layout[variant].sections;
                      return (
                        <div key={`${layoutKey}_${variant}`} className="rounded-md border border-gray-200 p-3 bg-gray-50/60">
                          <div className="flex items-center justify-between mb-2">
                            <strong className="text-sm">{title}</strong>
                            <select
                              className="border rounded px-2 py-1 text-sm"
                              value={sections.length}
                              onChange={(e) => updateLayoutSectionCount(layoutKey, variant, e.target.value)}
                              disabled={fieldsLocked}
                            >
                              <option value={1}>{t('CompanySettings.Sections.one')}</option>
                              <option value={2}>{t('CompanySettings.Sections.two')}</option>
                              <option value={3}>{t('CompanySettings.Sections.three')}</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {sections.map((sec, idx) => {
                              const slot = sec.image_slot || `${layoutKey}_${variant}_${idx + 1}`;
                              const uploaded = (formik.values.print_assets || {})[slot];
                              const uploadedUrl = (printAssetPreviewUrls || {})[slot];
                              return (
                                <div key={`${layoutKey}_${variant}_${idx}`} className="grid grid-cols-1 gap-3 border border-gray-200 rounded-md p-3 bg-white/90">
                                  <div className="grid grid-cols-4 gap-3">
                                    <label className="text-xs text-gray-500">{t('CompanySettings.Grid.Content')}</label>
                                    <label className="text-xs text-gray-500">{t('CompanySettings.Grid.WidthPct')}</label>
                                    <label className="text-xs text-gray-500">{t('CompanySettings.Grid.AlignH')}</label>
                                    <label className="text-xs text-gray-500">{t('CompanySettings.Grid.AlignV')}</label>

                                    <select
                                      className="w-full h-10 border rounded px-2 text-sm"
                                      value={sec.type}
                                      onChange={(e) => updateLayoutSection(layoutKey, variant, idx, { type: e.target.value })}
                                      disabled={fieldsLocked}
                                    >
                                      {printSectionTypes.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                    <InputText
                                      type="number"
                                      min={1}
                                      max={100}
                                      value={sec.width_pct ?? 100}
                                      onChange={(e) => updateLayoutSection(layoutKey, variant, idx, { width_pct: Math.max(1, Math.min(100, Number(e.target.value) || 0)) || 100 })}
                                      disabled={fieldsLocked}
                                      className="w-full h-10 border border-gray-300 rounded-md"
                                    />
                                    <select
                                      className="w-full h-10 border rounded px-2 text-sm"
                                      value={sec.align}
                                      onChange={(e) => updateLayoutSection(layoutKey, variant, idx, { align: e.target.value })}
                                      disabled={fieldsLocked}
                                    >
                                      <option value="left">{t('CompanySettings.Align.left')}</option>
                                      <option value="center">{t('CompanySettings.Align.center')}</option>
                                      <option value="right">{t('CompanySettings.Align.right')}</option>
                                    </select>
                                    <select
                                      className="w-full h-10 border rounded px-2 text-sm"
                                      value={sec.valign || 'top'}
                                      onChange={(e) => updateLayoutSection(layoutKey, variant, idx, { valign: e.target.value })}
                                      disabled={fieldsLocked}
                                    >
                                      <option value="top">{t('CompanySettings.Valign.top')}</option>
                                      <option value="middle">{t('CompanySettings.Valign.middle')}</option>
                                      <option value="bottom">{t('CompanySettings.Valign.bottom')}</option>
                                    </select>
                                  </div>
                                  <div>
                                    {sec.type === 'text' ? (
                                      <div>
                                        <label className="text-xs text-gray-500 mb-1 block">{t('CompanySettings.SectionTextLabel')}</label>
                                        <InputTextarea
                                          value={sec.text ?? ''}
                                          onChange={(e) => updateLayoutSection(layoutKey, variant, idx, { text: e.target.value })}
                                          disabled={fieldsLocked}
                                          rows={2}
                                          className="w-full border border-gray-300 rounded-md"
                                        />
                                      </div>
                                    ) : sec.type === 'image' ? (
                                      <div>
                                        <label className="text-xs text-gray-500 mb-1 block">{t('CompanySettings.SectionImageLabel')}</label>
                                        <div className="flex flex-wrap items-center gap-2">
                                          <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                            className="text-xs"
                                            disabled={fieldsLocked || recordId == null || uploadingPrintAssetSlot === slot}
                                            onChange={(e) => {
                                              const f = e.target.files?.[0];
                                              if (f) handlePrintAssetUpload(layoutKey, variant, idx, f);
                                              e.currentTarget.value = '';
                                            }}
                                          />
                                          <Button
                                            type="button"
                                            label={t('Common.Delete')}
                                            className="p-button-text p-button-sm p-2"
                                            onClick={() => handlePrintAssetDelete(slot)}
                                            disabled={fieldsLocked || !uploaded || uploadingPrintAssetSlot === slot}
                                          />
                                          <span className="text-xs text-gray-500 truncate max-w-[280px]">{uploaded ? `${t('CompanySettings.UploadedPrefix')} ${uploaded}` : t('CompanySettings.NoImage')}</span>
                                        </div>
                                        {uploadedUrl ? (
                                          <div className="mt-2 rounded border border-gray-200 bg-white p-2 inline-flex items-center justify-center min-h-[48px]">
                                            <img src={uploadedUrl} alt={t('CompanySettings.Alt.PrintAssetPreview')} className="max-h-10 w-auto object-contain" />
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : sec.type === 'empty' ? (
                                      <div className="flex items-end text-xs text-gray-400">{t('CompanySettings.NoContent')}</div>
                                    ) : (
                                      <div className="flex items-end text-xs text-gray-500">
                                        {sec.type === 'print_date'
                                          ? t('CompanySettings.DynamicType.print_date')
                                          : sec.type === 'page_x'
                                            ? t('CompanySettings.DynamicType.page_x')
                                            : t('CompanySettings.DynamicType.page_xx')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>

              <div className="xl:col-span-7 xl:self-start xl:sticky xl:top-4">
                <section className="p-6 bg-white rounded-md shadow-md w-full">
                  <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('CompanySettings.Section.PrintPreview')}</h2>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('CompanySettings.Section.PrintPreviewSubtitle')}
                  </p>
                  <div className="rounded-lg bg-zinc-100 p-4 border border-zinc-200 flex flex-col gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-2">{t('CompanySettings.Preview.FirstPage')}</div>
                      <PrintPreview values={formik.values} printAssetUrls={printAssetPreviewUrls} t={t} />
                    </div>
                    {formik.values.use_same_header_footer_all_pages !== false ? null : (
                      <div>
                        <div className="text-xs text-gray-500 mb-2">{t('CompanySettings.Preview.OtherPages')}</div>
                        <PrintPreview values={{ ...formik.values, __previewVariant: 'other' }} printAssetUrls={printAssetPreviewUrls} t={t} />
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
